import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import Button from '../../buttons/Button/Button';
import SecondaryButton from '../../buttons/SecondaryButton/SecondaryButton';
import Link from '../../links/Link/Link';

/**
 * A component for displaying the menu and account information
 */
function Navigation({
  hasAuthentication,
  isAuthenticated,
  name,
  profileHref,
  menuLinks,
  helperLinks,
  onLogin,
  onLogout
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(false);

  const toggleButtonRef = useRef(null);

  // Allow users to close the menu by hitting the escape key.
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.keyCode === 27) {
        setIsOpen(false);
        toggleButtonRef.current.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useLayoutEffect(() => {
    setIsMobileViewport(window.innerWidth < 960);
  }, [window.innerWidth]);

  return (
    <nav
      aria-label={t('styleguide:layout.header.primaryNavigation')}
      className={classNames('primary', { 'nav--is-open': isOpen })}
    >
      <div className="nav__toggle">
        {hasAuthentication && !isAuthenticated && (
          <SecondaryButton className={'helper-menu__sign-in'} onPress={onLogin}>
            {t('styleguide:sign_in')}
          </SecondaryButton>
        )}
        {hasAuthentication && isAuthenticated && (
          <>
            <a
              href={profileHref}
              className={
                'button button--secondary button--icon icon--user icon--left button--as-link ignore-external helper-menu__account'
              }
            >
              {name}
            </a>
            <SecondaryButton className={'helper-menu__sign-out'} onPress={onLogout}>
              {t('styleguide:sign_out')}
            </SecondaryButton>
          </>
        )}
        <Button
          ref={toggleButtonRef}
          className="toggle__button"
          onPress={() => setIsOpen(!isOpen)}
          ariaExpanded={isOpen}
        >
          <span></span>
          <span></span>
          <span></span>
          {t('styleguide:navigation_menu_button_text')}
        </Button>
      </div>
      <div className="nav__content">
        <ul aria-hidden={!isOpen && isMobileViewport ? true : undefined}>
          {[...menuLinks, ...helperLinks].map((link, index) => {
            return (
              <li key={index}>
                <Link
                  href={link.href}
                  className={classNames({ 'is-helper': index >= menuLinks.length })}
                  tabIndex={!isOpen && isMobileViewport ? '-1' : undefined}
                  onKeyDown={(e) => {
                    // Trap focus inside the menu when tabbing #14764
                    // https://dev.azure.com/ccdg/CWO%20-%20Accessibility/_workitems/edit/14764
                    if (index === 0 && e.shiftKey && e.keyCode === 9) {
                      console.log('first');
                      e.preventDefault();
                      toggleButtonRef.current.focus();
                    }

                    if (index === [...menuLinks, ...helperLinks].length - 1 && e.which === 9 && !e.shiftKey) {
                      console.log('last');
                      e.preventDefault();
                      toggleButtonRef.current.focus();
                    }
                  }}
                >
                  {link.text}
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
}

Navigation.propTypes = {
  /**
   * The authentication flag
   */
  hasAuthentication: PropTypes.bool,
  /**
   * The flag to determine authentication controls
   */
  isAuthenticated: PropTypes.bool.isRequired,
  /**
   * The username to display
   */
  name: PropTypes.string,
  /**
   * The destination of the profile link
   */
  profileHref: PropTypes.string,
  /**
   * The collection of main menu links
   */
  menuLinks: PropTypes.arrayOf(
    PropTypes.shape({
      href: PropTypes.string,
      text: PropTypes.string
    })
  ),
  /**
   * The collection of helper menu links
   */
  helperLinks: PropTypes.arrayOf(
    PropTypes.shape({
      href: PropTypes.string,
      text: PropTypes.string
    })
  ),
  /**
   * The login handler
   */
  onLogin: PropTypes.func,
  /**
   * The logout handler
   */
  onLogout: PropTypes.func
};

export default Navigation;
